import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useAdminCollection, useAdminSignals, useRunLineage, useSignalTrace, type SignalKey, type StoredSignal } from "../api/hooks";
import { Button, Chip, Empty, ErrorBox, Kpi, Loading, Page, Table, statusTone } from "../components/ui";
import { metaFor } from "../lib/connectors";
import { fmtDate, fmtDateTime, fmtNumber, relative, title } from "../lib/format";

export const Route = createFileRoute("/admin_/$tenantId")({ component: CollectionLog });

/** MVP-G3: one organisation's collection log — what is connected, what we hold by kind,
 *  and every sync run, newest first. This is the audit trail; lineage (G4) hangs off it. */
function CollectionLog() {
  const { tenantId } = Route.useParams();
  const q = useAdminCollection(tenantId);
  const [tracedRun, setTracedRun] = useState<string | null>(null);
  const [tracedSignal, setTracedSignal] = useState<SignalKey | null>(null);
  if (q.isPending) return <Page title="Collection log"><Loading /></Page>;
  if (q.isError) return <Page title="Collection log"><ErrorBox error={q.error} /></Page>;
  const { tenant, counts, connections, runs } = q.data;
  return (
    <Page
      title={tenant.name}
      sub={`${tenant.slug} · ${tenant.people} people · last collected ${tenant.last_sync_at ? relative(tenant.last_sync_at) : "never"}`}
      actions={<Link to="/admin" className="btn btn-light">← All organisations</Link>}
    >
      <h3 className="text-[15px] font-bold mb-2">What we hold</h3>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Kpi label="People" value={counts.people.toLocaleString()} hint="from the HRIS or a CSV import" />
        <Kpi
          label="Vendor accounts"
          value={counts.identities.toLocaleString()}
          tone={counts.identities_unmatched > 0 ? "elevated" : undefined}
          hint={counts.identities_unmatched > 0 ? `${counts.identities_unmatched} not yet matched to a person` : "all matched to a person"}
        />
        <Kpi label="Work items" value={counts.work_items.toLocaleString()} hint="pull requests, issues, tickets, deals" />
        <Kpi label="Signals" value={counts.signals.toLocaleString()} hint="computed by the nightly job" />
        <Kpi label="Message activity" value={counts.message_activity.toLocaleString()} hint="hourly counts per person; never text" />
        <Kpi label="Calendar events" value={counts.calendar_events.toLocaleString()} hint="times and attendee counts; never titles" />
      </div>

      <h3 className="text-[15px] font-bold mb-2">Connections</h3>
      {connections.length === 0 ? (
        <Empty title="Nothing connected" body="This organisation has not connected a tool yet." />
      ) : (
        <Table
          rows={connections}
          keyOf={(c) => c.id}
          cols={[
            { h: "App", cell: (c) => <><span className="font-semibold">{metaFor(c.connector).name}</span><span className="sub">{metaFor(c.connector).family}{c.external_workspace_id ? ` · ${c.external_workspace_id}` : ""}</span></> },
            { h: "Status", cell: (c) => <Chip tone={statusTone(c.status)}>{title(c.status)}</Chip>, w: "120px" },
            { h: "Credentials", cell: (c) => <span className="text-muted">{c.has_credentials ? "stored, encrypted" : "none"}</span>, w: "150px" },
            { h: "Scopes", cell: (c) => <span className="text-muted text-[12px]">{c.scopes.length ? c.scopes.join(", ") : "—"}</span> },
            { h: "Updated", cell: (c) => <span className="text-muted">{relative(c.updated_at)}</span>, w: "120px" },
          ]}
        />
      )}

      <h3 className="text-[15px] font-bold mt-6 mb-2">Signals</h3>
      <p className="text-muted text-[12.5px] mb-2">Every number the nightly job stored, newest window first, with the privacy floor it was computed under. Greyed rows are under the floor: the product never shows them, and they are the ones to notice before a customer does.</p>
      {tracedSignal && <div className="mb-3"><SignalTrace tenantId={tenantId} sig={tracedSignal} onClose={() => setTracedSignal(null)} onRun={(id) => { setTracedSignal(null); setTracedRun(id); }} /></div>}
      <Signals tenantId={tenantId} onTrace={(s) => { setTracedRun(null); setTracedSignal(s); }} traced={tracedSignal} />

      <h3 className="text-[15px] font-bold mt-6 mb-2">Collection log</h3>
      <p className="text-muted text-[12.5px] mb-2">Every sync run, newest first. A run is one connector fetching one kind of object; pages are vendor API pages, records are what we kept from them. Trace a run to see what it wrote and which signals it fed.</p>
      {tracedRun && <div className="mb-3"><RunTrace tenantId={tenantId} runId={tracedRun} onClose={() => setTracedRun(null)} onSignal={(s) => { setTracedRun(null); setTracedSignal(s); }} /></div>}
      {runs.length === 0 ? (
        <Empty title="No runs yet" body="Runs appear within 15 minutes of a connection going active." />
      ) : (
        <Table
          rows={runs}
          keyOf={(r) => r.id}
          cols={[
            { h: "Started", cell: (r) => <span className="text-muted whitespace-nowrap">{fmtDateTime(r.started_at)}</span>, w: "170px" },
            { h: "Connector", cell: (r) => <span className="font-semibold">{metaFor(r.connector).name}</span>, w: "140px" },
            { h: "Object", cell: (r) => <span>{title(r.object_type)}</span>, w: "140px" },
            { h: "Outcome", cell: (r) => <Chip tone={statusTone(r.status)}>{title(r.status)}</Chip>, w: "140px" },
            { h: "Pages", cell: (r) => <span className={r.pages ? "" : "text-muted"}>{r.pages}</span>, w: "80px" },
            { h: "Records", cell: (r) => <span className={r.records_ingested ? "font-semibold" : "text-muted"}>{r.records_ingested}</span>, w: "90px" },
            { h: "Waited", cell: (r) => <span className="text-muted">{r.rate_limited_seconds ? `${r.rate_limited_seconds}s` : "—"}</span>, w: "80px" },
            { h: "Error", cell: (r) => <span className="text-red text-[12px]">{r.error ?? ""}</span> },
            { h: "", cell: (r) => <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" onClick={() => { setTracedSignal(null); setTracedRun(r.id === tracedRun ? null : r.id); }}>{r.id === tracedRun ? "Hide" : "Trace →"}</Button>, w: "90px" },
          ]}
        />
      )}
    </Page>
  );
}

const sameSignal = (a: SignalKey, b: SignalKey) =>
  a.signal_key === b.signal_key && a.scope_type === b.scope_type && a.scope_key === b.scope_key && a.period_start === b.period_start && a.period_end === b.period_end;

function Signals({ tenantId, onTrace, traced }: { tenantId: string; onTrace: (s: SignalKey) => void; traced: SignalKey | null }) {
  const q = useAdminSignals(tenantId);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (q.data.length === 0) return <Empty title="No signals yet" body="The nightly job writes signals once it has a complete week of facts." />;
  return (
    <Table
      rows={q.data}
      keyOf={(s) => `${s.signal_key}|${s.scope_type}|${s.scope_key}|${s.period_start}|${s.period_end}`}
      cols={[
        { h: "Week", cell: (s) => <span className={`whitespace-nowrap ${s.visible ? "text-muted" : "text-text-4"}`}>{fmtDate(s.period_start)} – {fmtDate(s.period_end)}</span>, w: "170px" },
        { h: "Signal", cell: (s) => <span className={s.visible ? "font-semibold" : "text-text-4"}>{title(s.signal_key)}</span> },
        { h: "Scope", cell: (s) => <span className={s.visible ? "text-muted" : "text-text-4"}>{s.scope_type} · {s.scope_key}</span>, w: "160px" },
        { h: "Value", cell: (s) => <span className={s.visible ? "font-semibold" : "text-text-4"}>{fmtNumber(s.value)}</span>, w: "90px" },
        { h: "People", cell: (s) => <Floor s={s} />, w: "150px" },
        { h: "From", cell: (s) => <span className="text-muted text-[12px]">{s.inputs.map(title).join(", ")}</span> },
        { h: "", cell: (s) => <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" onClick={() => onTrace(s)}>{traced && sameSignal(traced, s) ? "Shown" : "Trace ←"}</Button>, w: "90px" },
      ]}
    />
  );
}

/** MVP-G5: the count behind a number and the floor it needed. */
function Floor({ s }: { s: StoredSignal }) {
  if (s.visible) return <span className="text-muted">{s.population_n} of ≥{s.min_population}</span>;
  return <Chip tone="elevated">{s.population_n} of ≥{s.min_population} · hidden</Chip>;
}

/** Backward: a signal → the rows in its window per input → the runs → the raw pages. */
function SignalTrace({ tenantId, sig, onClose, onRun }: { tenantId: string; sig: SignalKey; onClose: () => void; onRun: (runId: string) => void }) {
  const q = useSignalTrace(tenantId, sig);
  return (
    <div className="card border-accent-ui">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-bold text-[14px]">Where {title(sig.signal_key)} for {sig.scope_type} · {sig.scope_key} came from</div>
          <div className="text-muted text-[12px]">Week {fmtDate(sig.period_start)} – {fmtDate(sig.period_end)}</div>
        </div>
        <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" onClick={onClose}>Close</Button>
      </div>
      {q.isPending ? <div className="mt-3"><Loading /></div> : q.isError ? <div className="mt-3"><ErrorBox error={q.error} /></div> : (
        <div className="mt-3 grid gap-3">
          {q.data.inputs.map((i) => (
            <div key={i.table} className="border-t border-line pt-3">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold text-[13px]">{title(i.table)}</span>
                <span className="text-muted text-[12px]">{i.synced ? `${i.rows_in_window.toLocaleString()} rows in the window` : "entered in the product; no sync behind it"}</span>
              </div>
              {i.synced && (i.runs.length === 0 ? (
                <p className="text-muted text-[12px] mt-1">{i.rows_in_window > 0 ? "Rows in this window carry no run: they came from an import, a seed or a replay." : "No rows."}</p>
              ) : (
                <ul className="mt-1 text-[12.5px] space-y-1">
                  {i.runs.map((r) => (
                    <li key={r.id} className="flex items-center gap-2">
                      <Chip tone={statusTone(r.status)}>{title(r.status)}</Chip>
                      <span className="font-semibold">{metaFor(r.connector).name}</span>
                      <span className="text-muted">{title(r.object_type)} · {fmtDateTime(r.started_at)} · {r.pages} pages · {r.records_ingested} records</span>
                      <button className="link text-[12px]" onClick={() => onRun(r.id)}>Trace forward →</button>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          ))}
          <div className="border-t border-line pt-3">
            <div className="font-semibold text-[13px]">Raw pages <span className="text-muted font-normal">{q.data.raw_pages.length} in the lake</span></div>
            {q.data.raw_pages.length > 0 && (
              <ul className="mt-1 text-[11.5px] font-mono text-muted max-h-[160px] overflow-auto">
                {q.data.raw_pages.map((k) => <li key={k}>{k}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** Forward: a run → what it wrote → the signals downstream. */
function RunTrace({ tenantId, runId, onClose, onSignal }: { tenantId: string; runId: string; onClose: () => void; onSignal: (s: SignalKey) => void }) {
  const q = useRunLineage(tenantId, runId);
  return (
    <div className="card border-accent-ui">
      <div className="flex items-center justify-between gap-3">
        <div className="font-bold text-[14px]">What this run wrote, and what it fed</div>
        <Button variant="light" className="!py-1 !px-2.5 !text-[12px]" onClick={onClose}>Close</Button>
      </div>
      {q.isPending ? <div className="mt-3"><Loading /></div> : q.isError ? <div className="mt-3"><ErrorBox error={q.error} /></div> : (
        <div className="mt-3 grid gap-3">
          <div className="text-[12.5px] text-muted">
            <span className="font-semibold text-ink">{metaFor(q.data.run.connector).name}</span> · {title(q.data.run.object_type)} · {fmtDateTime(q.data.run.started_at)} · {q.data.run.pages} pages · {q.data.run.records_ingested} records
            {q.data.span_start && q.data.span_end && <> · facts from {fmtDate(q.data.span_start)} to {fmtDate(q.data.span_end)}</>}
          </div>
          <div className="border-t border-line pt-3">
            <div className="font-semibold text-[13px]">Wrote</div>
            {q.data.wrote.length === 0 && q.data.identities === 0 ? (
              <p className="text-muted text-[12px] mt-1">Nothing kept from this run: an empty page, or a run from before lineage was recorded.</p>
            ) : (
              <ul className="mt-1 text-[12.5px] space-y-0.5">
                {q.data.wrote.map((w) => <li key={w.table}><span className="font-semibold">{w.rows.toLocaleString()}</span> <span className="text-muted">{title(w.table)}</span></li>)}
                {q.data.identities > 0 && <li><span className="font-semibold">{q.data.identities.toLocaleString()}</span> <span className="text-muted">vendor accounts</span></li>}
              </ul>
            )}
          </div>
          <div className="border-t border-line pt-3">
            <div className="font-semibold text-[13px]">Signals downstream <span className="text-muted font-normal">{q.data.downstream.length}</span></div>
            {q.data.downstream.length === 0 ? (
              <p className="text-muted text-[12px] mt-1">None yet. The nightly job has not computed a window that covers these facts.</p>
            ) : (
              <ul className="mt-1 text-[12.5px] space-y-1">
                {q.data.downstream.map((s) => (
                  <li key={`${s.signal_key}|${s.scope_type}|${s.scope_key}|${s.period_start}`} className="flex items-center gap-2">
                    <span className={s.visible ? "font-semibold" : "text-text-4"}>{title(s.signal_key)}</span>
                    <span className="text-muted">{s.scope_type} · {s.scope_key} · {fmtDate(s.period_start)} – {fmtDate(s.period_end)} · {fmtNumber(s.value)}</span>
                    <Floor s={s} />
                    <button className="link text-[12px]" onClick={() => onSignal(s)}>Trace back ←</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-line pt-3">
            <div className="font-semibold text-[13px]">Raw pages <span className="text-muted font-normal">{q.data.run.s3_keys.length}</span></div>
            {q.data.run.s3_keys.length > 0 && (
              <ul className="mt-1 text-[11.5px] font-mono text-muted max-h-[120px] overflow-auto">
                {q.data.run.s3_keys.map((k) => <li key={k}>{k}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
